import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Store } from "@ngrx/store";
import { RouteReuseStrategy, Router, RouterLink } from "@angular/router";
import { DomSanitizer } from "@angular/platform-browser";
import { TranslateService, TranslateModule } from "@ngx-translate/core";
import { MenuModule } from "headlessui-angular";
import { FormsModule } from "@angular/forms";
import { NgClass, CommonModule } from "@angular/common";
import { IconModule } from "@los-workspace/common-lib";
import { AuthService } from "../services/auth.service";
import { ScreenLockService } from "../services/screen-lock.service";
import {
  NotificationsService,
  StaffNotification,
  openStaffNotificationCase,
} from "../services/notifications.service";

@Component({
  selector: "header",
  templateUrl: "./header.html",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    NgClass,
    RouterLink,
    FormsModule,
    MenuModule,
    TranslateModule,
    IconModule,
  ],
})
export class HeaderComponent implements OnInit {
  store: any;
  search = false;
  searchQuery = "";
  activeDropdown: string[] = [];
  @ViewChild("headerSearchInput")
  headerSearchInput?: ElementRef<HTMLInputElement>;
  staffNotifications: StaffNotification[] = [];
  unreadNotificationCount = 0;
  notificationsLoading = false;
  messages = [
    {
      id: 1,
      image: this.sanitizer.bypassSecurityTrustHtml(
        `<span class="grid place-content-center w-9 h-9 rounded-full bg-success-light dark:bg-success text-success dark:text-success-light"><svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg></span>`,
      ),
      title: "Congratulations!",
      message: "Your OS has been updated.",
      time: "1hr",
    },
    {
      id: 2,
      image: this.sanitizer.bypassSecurityTrustHtml(
        `<span class="grid place-content-center w-9 h-9 rounded-full bg-info-light dark:bg-info text-info dark:text-info-light"><svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg></span>`,
      ),
      title: "Did you know?",
      message: "You can switch between artboards.",
      time: "2hr",
    },
    {
      id: 3,
      image: this.sanitizer.bypassSecurityTrustHtml(
        `<span class="grid place-content-center w-9 h-9 rounded-full bg-danger-light dark:bg-danger text-danger dark:text-danger-light"> <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg></span>`,
      ),
      title: "Something went wrong!",
      message: "Send Reposrt",
      time: "2days",
    },
    {
      id: 4,
      image: this.sanitizer.bypassSecurityTrustHtml(
        `<span class="grid place-content-center w-9 h-9 rounded-full bg-warning-light dark:bg-warning text-warning dark:text-warning-light"><svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">    <circle cx="12" cy="12" r="10"></circle>    <line x1="12" y1="8" x2="12" y2="12"></line>    <line x1="12" y1="16" x2="12.01" y2="16"></line></svg></span>`,
      ),
      title: "Warning",
      message: "Your password strength is low.",
      time: "5days",
    },
  ];

  constructor(
    public readonly translate: TranslateService,
    public readonly storeData: Store<any>,
    public readonly router: Router,
    private readonly sanitizer: DomSanitizer,
    private readonly authService: AuthService,
    private readonly screenLock: ScreenLockService,
    private readonly notificationsService: NotificationsService,
    private readonly destroyRef: DestroyRef,
    private readonly routeReuseStrategy: RouteReuseStrategy,
  ) {}

  ngOnInit(): void {
    this.initStore();
    this.loadUnreadNotificationCount();
  }

  async initStore() {
    this.storeData
      .select((d) => d.index)
      .subscribe((d) => {
        this.store = d;
      });
  }

  get userDisplayName(): string {
    return this.authService.displayName();
  }

  get userDisplayEmail(): string {
    return this.authService.displayEmail();
  }

  get userDisplayEmployeeId(): string {
    return this.authService.displayEmployeeId();
  }

  logout(): void {
    this.authService.logout();
  }

  lockScreen(): void {
    const returnUrl = this.router.url || "/dashboard";
    this.screenLock.lock(returnUrl);
    void this.router.navigateByUrl("/auth/lock-screen");
  }

  loadNotificationPanel(): void {
    this.loadUnreadNotificationCount();
    this.notificationsLoading = true;
    this.notificationsService
      .list({ page: 1, limit: 10 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (page) => {
          this.staffNotifications = page.items;
          this.notificationsLoading = false;
        },
        error: () => {
          this.staffNotifications = [];
          this.notificationsLoading = false;
        },
      });
  }

  loadUnreadNotificationCount(): void {
    this.notificationsService
      .unreadCount()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (count) => {
          this.unreadNotificationCount = count;
        },
        error: () => {
          this.unreadNotificationCount = 0;
        },
      });
  }

  openStaffNotification(notification: StaffNotification): void {
    if (!notification.readAt) {
      this.notificationsService
        .markRead(notification.id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (updated) => {
            this.staffNotifications = this.staffNotifications.map((item) =>
              item.id === notification.id ? updated ?? item : item,
            );
            if (this.unreadNotificationCount > 0) {
              this.unreadNotificationCount -= 1;
            }
          },
        });
    }
    openStaffNotificationCase(
      this.router,
      notification,
      this.routeReuseStrategy,
    );
  }

  markAllNotificationsRead(): void {
    this.notificationsService
      .markAllRead()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          const readAt = new Date().toISOString();
          this.staffNotifications = this.staffNotifications.map((item) =>
            item.readAt ? item : { ...item, readAt },
          );
          this.unreadNotificationCount = 0;
        },
      });
  }

  isUnreadNotification(notification: StaffNotification): boolean {
    return !notification.readAt;
  }

  formatNotificationWhen(createdAt: string): string {
    const date = new Date(createdAt);
    if (Number.isNaN(date.getTime())) {
      return createdAt;
    }
    const seconds = Math.round((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) {
      return "Just now";
    }
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) {
      return `${minutes} min ago`;
    }
    const hours = Math.round(minutes / 60);
    if (hours < 24) {
      return `${hours}h ago`;
    }
    const days = Math.round(hours / 24);
    return `${days}d ago`;
  }

  toggleSearch(): void {
    if (this.search) {
      this.closeSearch();
      return;
    }
    this.openSearch();
  }

  openSearch(): void {
    this.search = true;
    setTimeout(() => this.headerSearchInput?.nativeElement.focus(), 220);
  }

  closeSearch(): void {
    this.search = false;
    this.searchQuery = "";
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    if (!this.search) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (target?.closest(".header-expandable-search")) {
      return;
    }
    this.closeSearch();
  }

  toggleDropdown(value: string) {
    if (this.activeDropdown.includes(value)) {
      this.activeDropdown = this.activeDropdown.filter((d) => d !== value);
    } else {
      this.activeDropdown.push(value);
    }
  }
}
